import React, { useEffect, useState } from 'react';
import { Typography, Box, Skeleton, Tooltip } from '@mui/material';
import { weiToMoani } from '../utils/moaniUtils';
import { moaniPriceService } from '../services/MoaniPriceService';

/**
 * Pluggable MOANI Display Component
 * Shows MOANI amount in format: $M {tokenAmount} (tokensInUSD)
 * 
 * @param {string|number} amount - MOANI amount in Wei or MOANI units
 * @param {boolean} isWei - Whether the amount is in Wei (default: true)
 * @param {number} decimals - Number of decimal places for MOANI (default: 2)
 * @param {number} usdDecimals - Number of decimal places for USD (default: 4)
 * @param {string} variant - Typography variant (default: 'body2')
 * @param {string} color - Typography color (default: 'primary')
 * @param {boolean} showTooltip - Show detailed tooltip (default: false)
 * @param {object} sx - Additional MUI sx props
 * @param {React.Component} component - Custom wrapper component
 */
function MoaniDisplay({ 
  amount, 
  isWei = true,
  decimals = 2,
  usdDecimals = 4,
  variant = 'body2',
  color = 'primary',
  showTooltip = false,
  sx = {},
  component: Component = Typography,
  ...otherProps
}) {
  const [price, setPrice] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial price from cache
    const cachedPrice = moaniPriceService.getPriceSync();
    setPrice(cachedPrice);
    setLoading(false);

    // Fetch fresh price
    moaniPriceService.getPrice()
      .then(freshPrice => {
        setPrice(freshPrice);
        setLoading(false);
      })
      .catch(error => {
        console.warn('Failed to fetch MOANI price:', error);
        setLoading(false);
      });
  }, []);

  // Convert amount to MOANI if needed
  const moaniAmount = isWei ? weiToMoani(amount) : parseFloat(amount) || 0;
  
  // Calculate USD value
  const usdValue = price ? moaniAmount * price : 0;

  // Format amounts with comma separators and no decimals
  const formattedMoani = moaniAmount.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });

  const formattedUSD = usdValue.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    style: 'currency',
    currency: 'USD'
  });

  // Handle zero or invalid amounts
  if (moaniAmount === 0) {
    return (
      <Component 
        variant={variant} 
        color="text.secondary" 
        sx={sx}
        {...otherProps}
      >
        <Box component="span">
          0 $MOANI <Box component="span" sx={{ color: 'text.disabled' }}>($0)</Box>
        </Box>
      </Component>
    );
  }

  // Create tooltip content if enabled
  const tooltipContent = showTooltip ? (
    <Box>
      <Typography variant="body2">MOANI Amount: {formattedMoani}</Typography>
      <Typography variant="body2">USD Value: {formattedUSD}</Typography>
      <Typography variant="caption" color="text.secondary">
        Price: ${price?.toFixed(6) || '0.002500'} per MOANI
      </Typography>
      {loading && (
        <Typography variant="caption" color="text.secondary" display="block">
          Updating price...
        </Typography>
      )}
    </Box>
  ) : null;

  const content = (
    <Component 
      variant={variant} 
      color={color} 
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        ...sx
      }}
      {...otherProps}
    >
      {loading && price === null ? (
        <Skeleton width={120} height={20} />
      ) : (
        <Box component="span">
          {formattedMoani} $MOANI <Box component="span" sx={{ color: 'text.disabled' }}>({formattedUSD})</Box>
        </Box>
      )}
    </Component>
  );

  return showTooltip ? (
    <Tooltip title={tooltipContent} arrow>
      {content}
    </Tooltip>
  ) : content;
}

/**
 * Simplified MOANI display for inline use
 */
export function InlineMoaniDisplay({ amount, isWei = true, ...props }) {
  return (
    <MoaniDisplay
      amount={amount}
      isWei={isWei}
      variant="inherit"
      component="span"
      {...props}
    />
  );
}

/**
 * MOANI display with tooltip for detailed view
 */
export function DetailedMoaniDisplay({ amount, isWei = true, ...props }) {
  return (
    <MoaniDisplay
      amount={amount}
      isWei={isWei}
      showTooltip={true}
      {...props}
    />
  );
}

/**
 * Large MOANI display for headers/emphasis
 */
export function LargeMoaniDisplay({ amount, isWei = true, ...props }) {
  return (
    <MoaniDisplay
      amount={amount}
      isWei={isWei}
      variant="h6"
      decimals={4}
      usdDecimals={2}
      showTooltip={true}
      {...props}
    />
  );
}

export default MoaniDisplay;
